import React from 'react';
import * as bd from 'react-basic-design';
import classNames from 'classnames';
import { useField } from 'formik';
import { T, translate } from '../basic/text';
import { useT } from '../../i18n';

/*
    Usage:

        <FormikTextField type="text"     label="user-name"  name="userName" />
        <FormikTextField type="number"   label="age"        name="age" />
        <FormikTextField type="textarea" label="multi line" name="description" />

        <FormikTextField
            type="select"
            label="select city"
            name="cityId"
            items={[
                { id: 1, title: "Tehran" },
                { id: 2, title: "Mazandaran" },
                { id: 3, title: "Tabriz" },
            ]}
        >/

*/

type FormikTextFieldProps = {
    isHidden?: boolean;
    label?: string | React.ReactNode;
    labelWidth?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
    maxLabelWidth?: number | string;

    postfixLabel?: string | React.ReactNode;
    footer?: string | React.ReactNode;

    inputRef?: any;
    id?: string;
    type?:
        | 'textarea'
        | 'select'
        | 'text'
        | 'number'
        | 'email'
        | 'password'
        | 'search'
        | 'time'
        | 'url'
        | 'tel';
    items?: any[];
    inputClassName?: string;
    autoComplete?: 'on' | 'off';
    autoFocus?: boolean;
    spellCheck?: boolean;
    readOnly?: boolean;

    buttonIcon?: string | React.ReactNode;
    buttonOnClick?: bd.Void;

    handleEnter?: boolean;

    maxLength?: number | string;
    className?: string;
    width?: number | string;
    maxWidth?: number | string;
    inputMaxWidth?: number | string;
    inputStyle?: React.CSSProperties;
    controlClassName?: string;
    onPaste?: (ev: any) => void;
    [x: string]: any;
};

export const FormikTextField = ({
    isHidden,
    label,
    labelWidth,
    maxLabelWidth,

    postfixLabel,
    footer,

    inputRef,
    id,
    type = 'text',
    items,
    inputClassName,
    autoComplete,
    autoFocus,
    spellCheck,
    readOnly,

    buttonIcon,
    buttonOnClick,

    handleEnter,

    maxLength,
    className,
    width,
    maxWidth,
    inputMaxWidth,
    inputStyle,
    controlClassName,
    onPaste,
    ...props
}: FormikTextFieldProps) => {
    if (handleEnter && type === 'textarea') handleEnter = false;
    const { t } = useT();
    const [field, meta] = useField(props as any);
    if (isHidden) return null;
    if (items && items.length > 0) type = 'select';
    if (!autoComplete) autoComplete = 'off';
    if (!spellCheck) spellCheck = false;
    if (label && id === undefined) id = props.name;

    const displayValue = field.value;

    const onChangeHandler = (e: any) => {
        field.onChange(e);
        if (props.onChange) props.onChange(e);
    };

    const onKeyDownHandler = (event: any) => {
        if (event.key.toLowerCase() === 'enter') {
            const form = event.target.form;
            if (!form) return;
            const index = [...form].indexOf(event.target);
            if (event.shiftKey) {
                if (index > 0) form.elements[index - 1].focus();
            } else {
                if (index + 1 < form.elements.length) form.elements[index + 1].focus();
            }
            event.preventDefault();
        }
    };

    if (!!inputMaxWidth) inputStyle = { ...inputStyle, maxWidth: inputMaxWidth };

    meta.error = translate(meta.error, null, t);

    if (!!footer) {
        postfixLabel = <small className="px-1 pt-1 text-muted">{footer}</small>;
        controlClassName += ' flex-column align-items-stretch';
    }

    return (
        <bd.FormRow
            label={typeof label === 'string' ? translate(label) : label}
            labelWidth={labelWidth}
            maxLabelWidth={maxLabelWidth}
            htmlFor={id}
            className={className}
            width={width}
            maxWidth={maxWidth}
            controlClassName={controlClassName}
        >
            <bd.TextField
                onKeyDown={handleEnter ? onKeyDownHandler : null}
                id={id}
                type={type as any}
                className={!!postfixLabel ? '' : 'w-100'}
                inputClassName={classNames(inputClassName, { 'bd-border-error': meta.error })}
                autoComplete={autoComplete}
                spellCheck={spellCheck}
                autoFocus={autoFocus}
                title={meta.error}
                {...field}
                {...props}
                value={displayValue || ''}
                inputStyle={inputStyle}
                buttonIcon={buttonIcon}
                buttonOnClick={buttonOnClick}
                readOnly={readOnly}
                error={meta.touched ? meta.error : undefined}
                showErrorLabel={meta.touched}
                onChange={onChangeHandler}
                inputRef={inputRef}
                maxLength={maxLength}
                items={items}
                onPaste={onPaste}
            />

            {postfixLabel}
        </bd.FormRow>
    );
};
